<template>
    <div v-if="isEnabled" ref="popup">
        <button class="pickup_button" v-on:click="open">See nearby inventory</button>

        <div v-if="isPopupVisible" id="select_store_popup"
             class="ssp_popup shown" :class="{keyboard_shown: isOnscreenKeyboardShown}">
            <div class="ssp_body">
                <button class="ssp_close js_close" v-on:click="close">
                    <span class="sr--only">{{options.labels.close}}</span>
                    <svg class="svg-icon" viewBox="0 0 20 20">
                        <path stroke="#333" stroke-width="2" d="M 5 5 L 15 15 M 5 15 L 15 5"></path>
                    </svg>
                </button>
                <p class="ssp_title">Nearby Inventory</p>
                <div class="ssp_data">
                    <add-to-cart v-if="mode != 'cart-details' && primaryCta.isAddToCart" :product="product"
                                 :options='addToCartOptions'
                                 :image-url="imageUrl"
                                 :button-disabled="buttonDisabled"
                                 :product-pricing="productPricing"
                                 v-on:addedtocart="$emit('addedtocart')"></add-to-cart>

                    <button v-if="mode == 'cart-details'" v-on:click="close" v-text="backButtonText" class="pd_button"></button>

                    <div class="results_box" :class="{loading: loading}">
                        <div class="awaiting_overlay partial" v-if="loading"></div>

                        <div v-if="!loading && results && results.length > 0" class="ssp_header">
                            <strong class="ssp_address_header">Store</strong>
                            <strong class="ssp_distance_header">Distance</strong>
                            <strong class="ssp_availability_header">Availability</strong>
                        </div>
                        <div v-if="!loading && results && results.length > 0" class="ssp_box">
                            <div v-for="branch in results" :key="branch.BranchId" class="ssp_store_item">
                                <span class="ssp_address">
                                    <strong>{{branch.BranchName}}</strong>
                                    <br />
                                    <span v-html="branch.FormattedAddress"></span>
                                </span>
                                <span class="ssp_distance">{{branch.Distance | toMiles}}</span>
                                <span class="ssp_availability">{{branch.OnHand}} in stock</span>
                            </div>
                        </div>
                        <div v-if="!loading && results && results.length === 0" class="ssp_box">
                            <div class="ssp_no_results">
                                <p>You can still add this item to your cart.</p>
                                <p>Though not in stock, we'll work to get this to you as soon as possible.</p>
                            </div>
                        </div>
                    </div>
                    <div class="transfer_time_disclaimer">
                        RDO Equipment Co. transfers inventory between stores to accommodate customer needs. Transfer times vary between locations.
                    </div>

                    <button v-on:click="close" v-text="backButtonText" class="ssp_button secondary"></button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { Constants } from "../functions/constants";
    import { BranchUtils } from "../functions/branch-utils";
    import { ArrayUtils } from "../functions/array-utils";
    import popupMixin from "../mixins/popup-mixin";
    import cardMixin from "../mixins/card-mixin";
    import AddToCart from "../components/AddToCart";

    import '../filters/to-miles';

    const defaultLabels = {
        "checkOtherStores": "Check other stores",
        "search": "Search",
        "close": "Close"
    };

    export default {
        name: "CheckOtherStores",
        data: function () {
            return {
                loading: false,
                isPopupVisible: false,
                isOnlineBranch: false,
                myStore: {},
                isBusinessCustomer: false,
            }
        },
        mixins: [cardMixin, popupMixin],
        components: { AddToCart},
        props: {
            product: {
                type: Object,
                default: {}
            },
            productPricing: {
                type: Object,
                default: {}
            },
            mode: {
                type: String,
                default: ""
            },
            results: {
                type: Array,
                default: null
            },
            imageUrl: {
                type: String,
                default: ''
            },
            options: {
                type: Object,
                default: {
                    labels: defaultLabels
                }
            },
            addToCartOptions: {
                type: Object,
                default: {}
            },
            buttonDisabled: {
                type: Boolean,
                default: false
            },
            primaryCta: {
                type: Object,
                default: null
            }
        },
        computed: {
            model: function () {
                return {
                    customDict: this.product
                };
            },
            isOnscreenKeyboardShown() {
                return GlobalApp.isOnscreenKeyboardShown;
            },
            backButtonText() {
                return this.mode == 'listing' ? "Back to Listing" : this.mode == 'cart-details' ? "Back to Cart" : "Back to Details";
            },
            isEnabled: function () {
                const hasPrice = this.productPricing && this.productPricing.main;

                if (this.mode != 'cart-details' && this.isParts && !hasPrice)
                    return false;

                if (this.isPartWithSpecialAORValidation === false)
                    return true;

                let productLine = this.productLineWithValidation();

                if (productLine) {
                    if (this.isOnlineBranch && Constants.PRODUCT_LINES_WITH_99_LOGIN_EXCEPTION_VALIDATION.includes(productLine))
                        return true;
                    else if (this.isBusinessCustomer && !Constants.PRODUCT_LINES_WITH_99_LOGIN_EXCEPTION_VALIDATION.includes(productLine))
                        return true;
                    else if (Constants.PRODUCT_LINES_WITH_UNRESTRICTED_LOGIN_VALIDATION.includes(productLine))
                        return true;
                }

                return false;
            }
        },
        methods: {
            init: function () {
                var that = this;
                that.loading = true;

                return GlobalApp.MyStoreService.getMyStore()
                    .then((myStore) => {
                        that.myStore = myStore;
                    });
            },
            open: function () {
                var that = this;

                GlobalApp.EventHub.raiseEvent(Constants.EVENTS.CLOSE_ALL_POPUPS, this);
                GlobalApp.EventHub.raiseEvent(Constants.EVENTS.POPUP_OPENED);
                GlobalApp.EventHub.raiseEvent(Constants.EVENTS.CHECK_STORES_POPUP_OPENED);

                this.init()
                    .then(function () {
                        that.isPopupVisible = true;
                        that.liftPopupSection(true);
                        if (!that.results) {
                            that.branchSearchQuery();
                        } else {
                            that.loading = false;
                        }
                    });
            },
            close: function () {
                this.isPopupVisible = false;
                this.liftPopupSection(false);
                GlobalApp.EventHub.raiseEvent(Constants.EVENTS.POPUP_CLOSED, this);
            },
            branchSearchQuery: function () {
                var that = this;
                that.loading = true;

                var candidateBranchesPromise = this.getCandidateBranchesWithCentralPoint(this.myStore.Address);
                var getProductsAvailabilityPromise = GlobalApp.BranchService.getProductsAvailability([this.product.sku]);
                Promise.all([candidateBranchesPromise, getProductsAvailabilityPromise]).then((values) => {
                    var candidateBranches = values[0];
                    var availabilityData = values[1];

                    var branchesMatchingByAvailability = that.getBranchesMatchingProductListByAvailability(availabilityData);
                    var branchesMatchingCart = candidateBranches.Items.filter(function (branch) {
                        return branchesMatchingByAvailability.includes(branch.BranchId);
                    }).map(function (branch) {
                        var branchAvailability = availabilityData.Items.find(function (partAvailability) { return partAvailability.BranchExternalId == branch.BranchId})
                        if (branchAvailability) {
                            branch.OnHand = branchAvailability.OnHand;
                        }

                        return branch;
                    });

                    if (this.isOnlineBranch) {
                        branchesMatchingCart = branchesMatchingCart.sort((a, b) => b.OnHand - a.OnHand);
                    }

                    BranchUtils.decorateBranches(branchesMatchingCart);

                    that.results = branchesMatchingCart;
                    that.loading = false;
                    return branchesMatchingCart;
                });
            },
            getCandidateBranchesWithCentralPoint: async function (searchPosition) {
                var search_request = {
                    "equipmentTypes": [],
                    "pageNumber": "1",
                    "pageSize": null,
                    "searchRadius": Constants.BRANCH.DEFAULT_DISTANCE
                };

                var queryLocationFilters = await BranchUtils.getQueryLocationFilters(searchPosition);

                search_request = { ...search_request, ...queryLocationFilters.searchFilters, ...queryLocationFilters.distanceFilters };

                if (!this.isOnlineBranch) {
                    search_request.LatitudeForDistance = search_request.Latitude;
                    search_request.LongitudeForDistance = search_request.Longitude;
                }

                return await GlobalApp.BranchService.query(search_request);
            },
            getBranchesMatchingProductListByAvailability: function (availabilityData) {
                if (ArrayUtils.isNotEmpty(availabilityData.Items) === false)
                    return [];

                var matchingBranchIds = [];
                for (var i = 0; i < availabilityData.Items.length; i++) {
                    var branchPartAvailability = availabilityData.Items[i];
                    if (branchPartAvailability.OnHand > 0)
                        matchingBranchIds.push(branchPartAvailability.BranchExternalId);
                }

                return matchingBranchIds;
            }
        },
        watch: {
            'value': {
                handler: function (val) {
                    this.$emit('input', val)
                }
            }
        },
        async created() {
            this.init();

            this.isBusinessCustomer = await GlobalApp.CustomerService.isCurrentCustomerWithBusinessAccountType();
            GlobalApp.MyStoreService.isMyStoreOnlineBranch().then(this.updateIsOnlineBranch);
            GlobalApp.EventHub.subscribeToEvent(Constants.EVENTS.ADD_TO_CART_PROCESSING_OFF, this.close);
        },

        destroyed() {
            this.results = null;
            GlobalApp.EventHub.unsubscribeToEvent(Constants.EVENTS.ADD_TO_CART_PROCESSING_OFF, this.close);
        },
    };
</script>